import React, { useRef } from 'react';
import config from '$/config';
import isMobile from '$/utils/isMobile';
import { isLangRtl } from '$/shared/frontend/utils/i18n';
import { i18n, I18n } from '@lingui/core';
import { css } from '@emotion/react';

function isInsideElement(
  elementRef: React.RefObject<HTMLButtonElement>,
  event: MouseEvent,
  rtl: boolean
): boolean {
  if (!elementRef.current) return false;
  if (elementRef.current.children.length < 2) return false;

  const spanRect = elementRef.current.children[0].getBoundingClientRect();
  const imgRect = elementRef.current.children[1].getBoundingClientRect();
  if (rtl) {
    return (
      event.clientX >= imgRect.left &&
      event.clientX <= spanRect.right &&
      event.clientY >= imgRect.top &&
      event.clientY <= imgRect.bottom
    );
  }
  return (
    event.clientX >= spanRect.left &&
    event.clientX <= imgRect.right &&
    event.clientY >= imgRect.top &&
    event.clientY <= imgRect.bottom
  );
}

function isThroughId(event: MouseEvent, clickThroughIdsString: string): boolean {
  return Boolean(
    event.target && clickThroughIdsString.includes(`|${(event.target as HTMLElement).id}|`)
  );
}

interface LogoProps {
  heightPx: number;
  version?: 'icon' | 'normal' | 'white' | 'reversed' | 'grayed';
  noLink?: boolean;
  className?: string;
  href?: string;
}

const Logo: React.FC<LogoProps> = ({ heightPx: height, noLink, version, className, href }) => {
  let logo: string;
  let widthPx = height * 6.285714285714286;

  switch (version) {
    case 'reversed':
      logo = require('./logo_reversed.svg').default;
      break;
    case 'white':
      logo = require('./logo_white.svg').default;
      break;
    case 'grayed':
      logo = require('./logo_light_steel_blue.svg').default;
      break;
    case 'icon':
      logo = require('./icon.svg').default;
      widthPx = height;
      break;
    default:
    case 'normal':
      logo = require('./logo.svg').default;
      break;
  }

  const img = React.useMemo(
    () => (
      <img
        height={height}
        width={widthPx}
        className={`logo object-fit ${className}`}
        alt='eWebinar logo: The Best Webinar Software For Customer Success and Scaling Your Business'
        src={logo}
      />
    ),
    [height, widthPx, className, logo]
  );

  if (noLink) {
    return img;
  }

  return (
    <a
      href={href ? href : `https://${config.DOMAIN}`}
      target='_blank'
      data-event-label='eWebinar: The Best Webinar Software For Customer Success and Scaling Your Business'
    >
      {img}
    </a>
  );
};

interface PoweredByEWebinarProps {
  i18nProvided?: I18n;
  className?: string;
  hideBranding: boolean | undefined | null;
  logoVersion?: LogoProps['version'];
  teamId: string | undefined | null;
  // clickThroughId is used to make a certain object to allow the user to click through it
  // namely the cards-list
  clickThroughIds?: string[];
  size?: 'sm' | 'lg';
  vertical?: boolean;
}
export const PoweredByEWebinar: React.FC<PoweredByEWebinarProps> = ({
  i18nProvided = i18n,
  className,
  hideBranding,
  logoVersion = 'grayed',
  vertical,
  teamId,
  clickThroughIds,
  size = 'sm',
}) => {
  const href = teamId
    ? `${config.API_SERVER_URL}/v1/fpredirect?url=${encodeURIComponent(
        config.URL_MARKETING_SITE
      )}&tid=${teamId}`
    : config.URL_MARKETING_SITE;

  const componentRef = useRef(null);
  const rtl = isLangRtl(i18nProvided.locale);

  // Do everything based on a string of the array since the array may not be memoized and we
  // don't want useEffect running over and over
  const clickThroughIdsString = clickThroughIds ? `|${clickThroughIds.join('|')}|` : undefined;
  React.useEffect(() => {
    if (!clickThroughIdsString || hideBranding) return;

    let isMouseDownInsideElement = false;
    const mousedown = (event: MouseEvent): void => {
      //we cover only when it's above the #cards-list
      if (!isThroughId(event, clickThroughIdsString)) return;
      if (isInsideElement(componentRef, event, rtl)) {
        isMouseDownInsideElement = true;
      }
    };

    const mouseup = (event: MouseEvent): void => {
      if (isInsideElement(componentRef, event, rtl) && isMouseDownInsideElement) {
        window.open(href, '_blank');
      }
      isMouseDownInsideElement = false;
    };

    const setCursorPointer = (event: MouseEvent): void => {
      if (!isThroughId(event, clickThroughIdsString)) return;
      if (isInsideElement(componentRef, event, rtl)) {
        document.body.style.cursor = 'pointer';
      } else {
        document.body.style.cursor = 'auto';
      }
    };

    document.body.addEventListener('mousedown', mousedown);
    document.body.addEventListener('mouseup', mouseup);
    document.body.addEventListener('mousemove', setCursorPointer);

    return (): void => {
      document.body.removeEventListener('mousedown', mousedown);
      document.body.removeEventListener('mouseup', mouseup);
      document.body.addEventListener('mousemove', setCursorPointer);
    };
  }, [clickThroughIdsString, href, hideBranding, rtl]);

  const sizeRem = size === 'sm' ? (isMobile ? 0.75 : 0.875) : isMobile ? 1 : 1.1;
  return hideBranding ? (
    <div></div>
  ) : (
    <a
      href={href}
      target='_blank'
      data-event-label='eWebinar: The Best Webinar Software For Customer Success and Scaling Your Business'
      ref={componentRef}
      className={`${className}`}
      style={{ textDecoration: 'none' }}
      css={css`
        align-items: baseline;
        display: flex;
        justify-content: center;
        height: ${sizeRem}rem;
        ${isLangRtl(i18nProvided.locale) ? 'direction: rtl;' : 'direction: ltr;'}
        ${vertical
          ? `
            flex-direction: column;
            align-items: center;
            img {
              height: 1.5rem;
              width: auto;
              margin-top: 0.5rem;
            }
          `
          : ''}
      `}
    >
      <span
        css={css`
          font-size: ${sizeRem}rem;
          color: ${logoVersion === 'grayed' ? '#9aadaf' : '#000'};
          font-family: sans-serif;
          font-weight: 400;
          white-space: nowrap;
          position: relative;
        `}
      >
        {i18nProvided._({
          id: 'Powered by',
        })}
      </span>
      <Logo
        version={logoVersion}
        css={css`
          width: auto;
          height: ${sizeRem}rem; 
          margin-${isLangRtl(i18nProvided.locale) ? 'right' : 'left'}: 3px;
        `}
        heightPx={sizeRem * 14}
        noLink={true}
      />
    </a>
  );
};
export default Logo;
